'use client'

import { useSignals } from '@preact/signals-react/runtime'
import ClientSideSlider from '@susu/headless-commerce/components/SideSlider/ClientSideSlider'
import { useCountry } from '@susu/headless-commerce/contexts/country/client'
import { useLocale } from '@susu/headless-commerce/contexts/locale/client'
import { usePageType } from '@susu/headless-commerce/hooks/usePageType'
import { URLData } from '@susu/headless-commerce/utils/tracking/segment'
import { trackEvent } from '@susu/headless-commerce/utils/tracking/tracking'
import classNames from 'classnames'
import type { ReactNode } from 'react'
import { useClientNav } from '../ClientNavProvider'
import { NavSearchPanel } from '../NavSearchPanel'
import styles from './NavSearchButton.module.scss'

export type ClientNavSearchButtonProps = {
  children: ReactNode
  className?: string
  searchPanelOpenedFrom: 'header' | 'menu'
}

export const ClientNavSearchButton = ({
  children,
  className,
  searchPanelOpenedFrom,
}: ClientNavSearchButtonProps) => {
  useSignals()

  const locale = useLocale()
  const country = useCountry()
  const pageType = usePageType()
  const { url, locationId } = URLData()
  const { openSearchMenu, isSearchOpenFromHeader, setIsSearchOpenFromHeader } =
    useClientNav()

  const searchClass = classNames(
    styles['nav__search-btn'],
    'nav__search-btn',
    className,
    {
      [styles['nav__search-btn--header']]: searchPanelOpenedFrom === 'header',
      [styles['nav__search-btn--menu']]: searchPanelOpenedFrom === 'menu',
    },
  )

  const handleSearchClick = () => {
    if (searchPanelOpenedFrom === 'header') {
      setIsSearchOpenFromHeader(true)
    } else if (searchPanelOpenedFrom === 'menu') {
      openSearchMenu()
    }

    trackEvent({
      segment: {
        event: 'searchClicked',
        enrichProps: {
          locale,
          country,
        },
        properties: {
          eventCategory: 'global_interactions',
          eventLabel: '',
          eventLocation: searchPanelOpenedFrom === 'header' ? 'header' : 'menu',
          locationId,
          pageType,
          url,
        },
      },
    })
  }

  // Closing saerch panel from header.
  const handleSearchPanelHeaderClose = () => {
    if (searchPanelOpenedFrom === 'header') {
      setIsSearchOpenFromHeader(false)
    }
  }

  return (
    <>
      <button
        className={searchClass}
        type="button"
        onClick={handleSearchClick}
        data-testid="nav-search-button"
      >
        {children}
      </button>
      {searchPanelOpenedFrom === 'header' ? (
        <ClientSideSlider
          isOpen={isSearchOpenFromHeader.value}
          slideInFrom="left"
          fullScreen="mobile"
          hasCloseButton={true}
          onClose={handleSearchPanelHeaderClose}
          hideCloseButtonOnMobile={true}
        >
          <NavSearchPanel searchPanelOpenedFrom={searchPanelOpenedFrom} />
        </ClientSideSlider>
      ) : null}
    </>
  )
}
